/**
 * New Year drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawing_topics`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearDrawingsPack: ToilePack = {
    slug: 'new-year-drawings',
    category: 'coloring',
    name: 'New Year drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'numbers', label: '2027 fireworks', subject: 'the numbers 2027 in bubble shapes with fireworks bursting above'},
        {key: 'clock', label: 'Midnight clock', subject: 'a clock at midnight with balloons and streamers around it'},
        {key: 'party', label: 'Party hats', subject: 'children in party hats blowing horns under bunting and stars'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil sketch', style: 'a soft graphite pencil sketch with clear outlines and light hatching for shade', palette: 'Gray pencil on white paper, no color.', framing: 'Drawn on a plain white paper sheet, centered with a white margin.'},
        {key: 'crayon', label: 'Colour pencil', style: 'a colour pencil and crayon drawing, simple outlines filled with bright even strokes of colour', palette: 'Bright crayon colours on white paper.', framing: 'Drawn on a plain white drawing-book page, centered with a white margin.'},
        {key: 'outline', label: 'Outline to colour', style: 'a bold black outline drawing with flat marker colour filling each shape', palette: 'Black outlines with flat bright colour fills on white paper.', framing: 'Flat 2D drawing isolated on a pure white background with a white margin, clean closed black line work.'},
    ],
    directions: ['a cake', 'balloons', 'a countdown'],
    directionHint: 'try: a cake',
    seo: {
        title: 'New Year Drawings: 2027 Fireworks, Midnight Clock, Party',
        metaDescription: 'Nine New Year drawings: 2027 with fireworks, a clock at midnight and children in party hats, in pencil, colour and outline.',
        h1: 'New Year drawings: 2027 with fireworks, midnight clock, party hats',
        tagline: 'Three scenes, each in pencil sketch, colour pencil and outline to colour.',
        intro: 'New Year drawings for a school competition, a card or a classroom display. The set shows the numbers 2027 in bubble shapes with fireworks above, a clock at midnight with balloons and streamers, and children in party hats blowing horns under bunting and stars. Each scene comes as a pencil sketch, a colour pencil drawing and a bold outline to colour. Type a change in the direction field, such as a cake, balloons or a countdown.',
        useCases: [
            'a New Year card for school',
            'a classroom display for January',
            'a party decoration sheet',
            'a colouring page for a class',
        ],
        faq: [
            {q: 'Can I print these for a school competition?', a: 'Yes. Each picture is a plain drawing on a white background, so it prints on ordinary paper.'},
            {q: 'What is the difference between the three styles?', a: 'Pencil sketch is grey shading with no colour. Colour pencil is bright crayon colour. Outline to colour is a bold black line drawing with flat colour inside each shape, easy to fill in.'},
            {q: 'Can I use another year?', a: 'The nine pictures show 2027. Type another year in the direction field; it is a request, so the result may differ.'},
            {q: 'Can I ask for a different picture?', a: 'Yes. Type the change in the direction field, such as a countdown. It is a request, so the result may differ from the nine shown.'},
        ],
    },
}
